/**
 * Every Business Discovery on record, as a searchable list.
 *
 * INTERNAL. The same session list the dashboard reads, with each session's
 * discovery status for its company and stage. Search, filtering and sorting
 * happen in the browser over that list; the API is unchanged and returns
 * everything at once, so there is nothing to paginate.
 *
 * Each row can be deleted, after a confirmation. The row goes as soon as the
 * backend has removed the session; if it could not, the row stays and the
 * reason is shown.
 */

import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { deleteSession } from '../api/client';
import ConsoleLayout from './ConsoleLayout';
import NewDiscoveryLink from './NewDiscoveryLink';
import { Card, CopyLinkButton, EmptyState, ErrorState, LoadingState, PageHeader, StatusBadge, Value } from './consoleUi';
import {
  clientOf,
  companyOf,
  formatDateTime,
  formatDuration,
  lifecycleLabel,
  matchesSearch,
  progressOf,
  useDiscoverySessions,
} from './consoleData';
import { deleteWithConfirmation } from './sessionDeletion';
import '../styles/SessionsPage.css';

export default function DiscoveriesPage() {
  const { sessions, discoveries, status, error, reload, removeSession } = useDiscoverySessions();
  const [query, setQuery] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const onDelete = useCallback(async (session, company) => {
    const id = session.session_id;
    setDeleteError(null);
    setDeletingId(id);
    const result = await deleteWithConfirmation({
      sessionId: id,
      company,
      confirm: (message) => window.confirm(message),
      remove: deleteSession,
    });
    setDeletingId(null);
    if (result.outcome === 'deleted' || result.outcome === 'already_gone') removeSession(id);
    else if (result.outcome === 'failed') setDeleteError(result.message);
  }, [removeSession]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('newest');

  const statuses = useMemo(
    () => [...new Set(sessions.map((session) => session.lifecycle))],
    [sessions],
  );

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    let rows = sessions.filter(
      (session) => statusFilter === 'all' || session.lifecycle === statusFilter,
    );
    if (needle) {
      // Searching by the names on the form, not only by what the conversation
      // revealed: an operator looking for the link a client lost knows who
      // they are, and that client may never have got far enough for the
      // discovery to have extracted anything.
      rows = rows.filter((session) =>
        matchesSearch(session, discoveries[session.session_id], needle),
      );
    }
    return sort === 'oldest' ? [...rows].reverse() : rows;
  }, [sessions, discoveries, query, statusFilter, sort]);

  return (
    <ConsoleLayout title="Business Discoveries">
      <PageHeader
        eyebrow="Sales Console"
        title="Business Discoveries"
        subtitle={
          status === 'ready'
            ? `${sessions.length} ${sessions.length === 1 ? 'Business Discovery' : 'Business Discoveries'} on record`
            : 'Every Business Discovery on record'
        }
      />

      <NewDiscoveryLink onCreated={reload} />

      {error && status === 'ready' && (
        <p className="nsc-alert nsc-alert--danger" role="alert">{error}</p>
      )}
      {deleteError && (
        <p className="nsc-alert nsc-alert--danger" role="alert">{deleteError}</p>
      )}

      <Card className="nsc-card--flush">
        <div className="nsc-toolbar" role="search">
          <label className="nsc-field nsc-field--grow">
            <span className="nsc-field__label">Search</span>
            <input
              type="search"
              className="nsc-input"
              placeholder="Company, industry or reference"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <label className="nsc-field">
            <span className="nsc-field__label">Status</span>
            <select
              className="nsc-input"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">All statuses</option>
              {statuses.map((value) => (
                <option key={value} value={value}>{lifecycleLabel(value)}</option>
              ))}
            </select>
          </label>
          <label className="nsc-field">
            <span className="nsc-field__label">Sort</span>
            <select className="nsc-input" value={sort} onChange={(event) => setSort(event.target.value)}>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
          </label>
        </div>

        {status === 'loading' && <LoadingState label="Loading Business Discoveries…" />}

        {status === 'error' && (
          <ErrorState
            title="Business Discoveries could not be loaded"
            action={<button type="button" className="nsc-button nsc-button--secondary" onClick={reload}>Try again</button>}
          >
            {error}
          </ErrorState>
        )}

        {status === 'ready' && sessions.length === 0 && (
          <EmptyState title="No Business Discoveries yet">
            Create a client link to start the first one.
          </EmptyState>
        )}

        {status === 'ready' && sessions.length > 0 && visible.length === 0 && (
          <EmptyState title="No matches">
            No Business Discovery matches these filters.
          </EmptyState>
        )}

        {status === 'ready' && visible.length > 0 && (
          <>
            <p className="nsc-toolbar__count" aria-live="polite">
              Showing {visible.length} of {sessions.length}
            </p>
            <div className="nsc-table-wrap">
              <table className="nsc-table">
                <thead>
                  <tr>
                    <th scope="col">Company</th>
                    <th scope="col">Client</th>
                    <th scope="col">Status</th>
                    <th scope="col">Stage</th>
                    <th scope="col">Created</th>
                    <th scope="col">Report</th>
                    <th scope="col"><span className="nsc-sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((session) => {
                    const id = session.session_id;
                    const discovery = discoveries[id];
                    const progress = progressOf(discovery, session.current_layer);
                    const duration = formatDuration(session.duration_seconds);
                    return (
                      <tr key={id}>
                        <td data-label="Company">
                          <Link to={`/internal/sessions/${id}/discovery`} className="nsc-table__primary">
                            <Value value={companyOf(session, discovery)} />
                          </Link>
                          <span className="nsc-table__secondary nsc-mono">{id}</span>
                        </td>
                        <td data-label="Client"><Value value={clientOf(session)} /></td>
                        <td data-label="Status"><StatusBadge lifecycle={session.lifecycle} /></td>
                        <td data-label="Stage">{discovery === undefined ? '…' : progress.label}</td>
                        <td data-label="Created">
                          <span className="nsc-cell-stack">
                            <span>{formatDateTime(session.created_at)}</span>
                            <span className="nsc-table__secondary">
                              {duration ? `Duration ${duration}` : 'Duration not provided'}
                            </span>
                          </span>
                        </td>
                        <td data-label="Report">
                          {session.report_generated ? (
                            <Link to={`/internal/sessions/${id}/report`} className="nsc-link">View report</Link>
                          ) : (
                            <span className="nsc-empty-value">Not available</span>
                          )}
                        </td>
                        <td data-label="Actions" className="nsc-table__actions">
                          <Link to={`/internal/sessions/${id}/discovery`} className="nsc-link">Overview</Link>
                          <Link to={`/internal/sessions/${id}/transcript`} className="nsc-link">Transcript</Link>
                          <CopyLinkButton path={session.assessment_path} />
                          <button
                            type="button"
                            className="nsc-link nsc-link--button nsc-link--danger"
                            onClick={() => onDelete(session, companyOf(session, discovery))}
                            disabled={deletingId !== null}
                            aria-label={`Delete Business Discovery ${id}`}
                          >
                            {deletingId === id ? 'Deleting…' : 'Delete'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </ConsoleLayout>
  );
}
